import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const NotificationsPage = () => {
  const {
    editingTemplate, setEditingTemplate,
    notificationTemplates, setNotificationTemplates,
    showToast,
  } = useDashboard();

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-6 md:px-8 lg:px-12">
      {!editingTemplate ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Email Notifications</h2>
          </div>
          <p className="text-gray-500">Manage automated email notifications sent to clients and team members.</p>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {notificationTemplates.map((template) => (
                <div key={template.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <svg className={`w-5 h-5 ${template.is_active ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-500 truncate max-w-md">{template.subject}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={template.is_active}
                        onChange={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.patch(`${API}/notifications/templates/${template.id}/toggle`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setNotificationTemplates(prev => prev.map(t => 
                              t.id === template.id ? {...t, is_active: !t.is_active} : t
                            ));
                          } catch (err) {
                            showToast('Failed to update', 'error');
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
              {notificationTemplates.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <p>Loading notification templates...</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800">
                  <strong>Template Variables:</strong> Use {'{{client_name}}'}, {'{{property_address}}'}, {'{{appointment_date}}'}, {'{{business_name}}'}, etc. in your templates.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-6">
            <button 
              onClick={() => setEditingTemplate(null)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold">Edit: {editingTemplate.name}</h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
              <input
                type="text"
                value={editingTemplate.name}
                onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject</label>
              <input
                type="text"
                value={editingTemplate.subject}
                onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Appointment Confirmed - {{property_address}}"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
              <textarea
                value={editingTemplate.body}
                onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                rows={12}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder="Write your email template here..."
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {['{{client_name}}', '{{client_email}}', '{{property_address}}', '{{appointment_date}}', '{{appointment_time}}', '{{services}}', '{{photographer_name}}', '{{photographer_phone}}', '{{business_name}}', '{{business_phone}}', '{{business_website}}', '{{delivery_link}}', '{{reset_link}}'].map(v => (
                  <code key={v} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600">{v}</code>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingTemplate.is_active}
                  onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(`${API}/notifications/templates/${editingTemplate.id}`, {
                        name: editingTemplate.name,
                        subject: editingTemplate.subject,
                        body: editingTemplate.body,
                        is_active: editingTemplate.is_active
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setNotificationTemplates(prev => prev.map(t => 
                        t.id === editingTemplate.id ? editingTemplate : t
                      ));
                      showToast('Template saved!', 'success');
                      setEditingTemplate(null);
                    } catch (err) {
                      showToast('Failed to save template', 'error');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
